import { Component, OnInit, ViewChild, Output, EventEmitter, Optional, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { EmailTemplate } from 'src/app/models/email-template';
import * as XLSX from 'xlsx-js-style';

import { SharedService } from '../../../layouts/shared-service';
import { CustomerinfobygroupService } from '../../../services/customerinfobygroup.service';
import { EmailType, GroupAnalyticsEmailPricingDialogComponent, GroupAnalyticsEmailPricingDialogResult } from '../group-analytics-email-pricing-dialog/group-analytics-email-pricing-dialog.component';
import { DecimalPrecisionPipe } from 'src/app/shared/pipes/decimal/decimal-precision.pipe';
import { TagsService } from '../../../services/tags.service';
import { GroupAnalyticsGenerateDialogGridComponent } from '../group-analytics-generate-dialog-grid/group-analytics-generate-dialog-grid.component';
import { ScheduleEmailProps, ScheduleEmailRequest } from 'src/app/pages/group-analytics/group-analytics-email-pricing-dialog/schedule-email/schedule-email';
import { SnackBarService } from 'src/app/ui-services/snackbar/snackBar.service';
import { ScheduledEmailsService } from '../manage-scheduled-emails/scheduled-emails.service';
import { AppDialogWrapperService } from '../../../ni-components/app-dialog-wrapper/app-dialog-wrapper.service';

export type GroupAnalyticsGenerateDialogData = {
    customers: any[];
    emailTemplate: EmailTemplate;
    tagList?: string[];
};

@Component({
    providers: [SharedService, ScheduledEmailsService],
    selector: 'app-group-analytics-generate-dialog',
    styleUrls: ['./group-analytics-generate-dialog.component.scss'],
    templateUrl: './group-analytics-generate-dialog.component.html',
})
export class GroupAnalyticsGenerateDialogComponent implements OnInit, AfterViewInit {
    @ViewChild(GroupAnalyticsGenerateDialogGridComponent, { static: false }) groupAnalyticsGenerateDialogGridComponent!: GroupAnalyticsGenerateDialogGridComponent;

    // Public Members
    dataSources: any[] = [];
    filter = '';
    selectedCustomers: any[];
    downloading: boolean;
    emailing: boolean;
    exportedCustomersCount: number;
    public selectOptions: any;
    public editSettings: any;
    public toolbar: string[];
    public filterOptions = {
        immediateModeDelay: 1000,
        mode: 'Immediate',
    };
    tags: any[] = [];
    selectedTags: any[] = [];
    private selectedCustomerMap = new Map<number, boolean>();
    public manuallyCheckedCustomerIds = new Set<number>();
    manuallyUncheckedCustomerIds = new Set<number>();

    // Multiselect labels - initialized in constructor to avoid change detection errors
    tagsLabel = 'Select Tags';
    tagsPlaceholder = 'Search for tags';

    // Flag to control when multiselect should be rendered (Solution 3)
    isComponentReady = false;

    // Data property - will be set by dialog wrapper
    private _data: GroupAnalyticsGenerateDialogData;
    get data(): GroupAnalyticsGenerateDialogData {
        return this._data;
    }
    set data(value: GroupAnalyticsGenerateDialogData) {
        this._data = value;
        // Initialize data sources when data is set
        if (value && value.customers) {
            this.initializeDataSources();
        }
    }

    // EventEmitters for dialog communication
    @Output() closeDialog = new EventEmitter<void>();

    constructor(
        @Optional() private readonly sharedService: SharedService,
        private customerInfoByGroupService: CustomerinfobygroupService,
        private decimalPrecisionPipe: DecimalPrecisionPipe,
        private tagService: TagsService,
        private snackbarService: SnackBarService,
        private scheduledEmailsService: ScheduledEmailsService,
        private cdr: ChangeDetectorRef,
        private appDialogWrapperService: AppDialogWrapperService
    ) {}

    public ngOnInit(): void {
        // Don't load tags here to avoid change detection issues
    }

    ngAfterViewInit(): void {
        // Initialize data sources if data was set before ngAfterViewInit
        if (this.data && this.data.customers) {
            this.initializeDataSources();
        }
        
        // Load tags after view is initialized to avoid change detection errors
        setTimeout(() => {
            this.loadTags();
            // Mark component as ready to render multiselect
            this.isComponentReady = true;
            this.cdr.detectChanges();
        }, 0);
    }

    private initializeDataSources(): void {
        if (this.data && this.data.customers) {
            this.dataSources = this.data.customers;
            this.selectOptions = { persistSelection: true };
            this.editSettings = { allowDeleting: true };
            this.toolbar = ['Delete'];
            // Trigger change detection to update the grid
            this.cdr.detectChanges();
        }
    }

    onCancelClick(): void {
        this.closeDialog.emit();
    }

    getSelectedCustomers(): any[] {
        if (!this.data || !this.data.customers) {
            return [];
        }
        return this.data.customers.filter(c => {
            const manual = this.selectedCustomerMap.get(c.customerId);
            const tagMatched = this.doesCustomerMatchTags(c);

            if (manual === true) return true;
            if (manual === false) return false;
            return tagMatched;
        });
    }

    doesCustomerMatchTags(customer: any): boolean {
        if (!this.selectedTags || this.selectedTags.length === 0) {
            return false;
        }

        const selectedTagValues = this.selectedTags.map(t => t.value);

        return customer.tags?.some(tag => selectedTagValues.includes(tag.name));
    }

    onEmail() {
        if (!this.data) {
            return;
        }
        const selectedCustomers = this.getSelectedCustomers();

        const dialogWrapper = this.appDialogWrapperService.open<GroupAnalyticsEmailPricingDialogResult>({
            title: 'Email Pricing',
            component: GroupAnalyticsEmailPricingDialogComponent,
            data: {
                customers: selectedCustomers,
                emailTemplate: this.data.emailTemplate,
                tagList: this.selectedTags.map(tag => tag.value),
            },
            height: 'auto',
            width: '650px',
        });

        // Subscribe to the event emitters from the component instance
        dialogWrapper.dialogRef.afterOpened().subscribe(() => {
            const wrapperComponent = dialogWrapper.dialogRef.componentInstance as any;
            const emailPricingComponent = wrapperComponent?.getComponentInstance?.() as GroupAnalyticsEmailPricingDialogComponent;
            
            if (emailPricingComponent) {
                // Subscribe to emailPricingResult event (for Schedule Email and Send Email Now buttons)
                if (emailPricingComponent.emailPricingResult) {
                    emailPricingComponent.emailPricingResult.subscribe((result: GroupAnalyticsEmailPricingDialogResult) => {
                        // Close the dialog
                        dialogWrapper.close();
                        
                        // Process the result
                        if (!result.emailTemplate?.oid) {
                            this.snackbarService.showErrorSnackBar(
                                'Global Email Template should be setup in the Group Analytics Home page'
                            );
                            return;
                        }

                        const emailTemplate: EmailTemplate = {
                            emailContentHtml: result.emailTemplate.emailContentHtml,
                            fromAddress: result.emailTemplate.fromAddress,
                            groupId: this.sharedService.currentUser.groupId,
                            oid: result.emailTemplate?.oid,
                            replyTo: result.emailTemplate.replyTo,
                            subject: result.emailTemplate.subject,
                            logoAlignment: result.emailTemplate.logoAlignment,
                        };

                        if (
                            result.emailType === EmailType.Scheduled &&
                            result.schedule?.timezone === ''
                        ) {
                            this.snackbarService.showErrorSnackBar(
                                'timezone is required to schedule email'
                            );
                            return;
                        }

                        this.manageSendEmail(result);
                    });
                }
                
                // Subscribe to cancelDialog event
                if (emailPricingComponent.cancelDialog) {
                    emailPricingComponent.cancelDialog.subscribe(() => {
                        // Close the dialog without result
                        dialogWrapper.close();
                    });
                }
            }
        });
    }
    private async manageSendEmail(result: GroupAnalyticsEmailPricingDialogResult) {
        // Handle non-scheduled emails (RightNow)
        if (result.emailType !== EmailType.Scheduled) {
            await this.onSendEmail();
            return;
        }

        // Handle scheduled emails
        const shouldSendNow = result.sendNow === true;
        
        if (shouldSendNow) {
            // Send email immediately AND schedule for future occurrences
            await this.onSendEmail();
        }
        
        // Schedule for future occurrences (always for scheduled emails)
        this.onScheduleEmail(result.schedule);
    }
    async onGenerate() {
        this.downloading = true;
        this.exportedCustomersCount = 0;

        const selectedCustomers = this.getSelectedCustomers();

        const promises = selectedCustomers.map(
            async (selectedCustomer) => {
                const data = (await this.customerInfoByGroupService
                    .getGroupAnalytics(
                        this.sharedService.currentUser.groupId,
                        selectedCustomer.customerId
                    )
                    .toPromise()) as any;

                if (data.length > 0) {
                    const exportData: any[] = [];

                    this.populateExportDataForCustomer(
                        data,
                        exportData
                    );
                    await this.exportReportForCustomer(exportData, data[0].company);
                }
            }
        );

        await Promise.all(promises);
        this.downloading = false;
    }

    async onSendEmail() {
        this.emailing = true;
        const selectedCustomers = this.getSelectedCustomers();
        this.exportedCustomersCount = 0;

        const promises = selectedCustomers.map(async (selectedCustomer) =>
                this.scheduledEmailsService
                .sendGroupAnalyticsEmail(
                    this.sharedService.currentUser.groupId,
                    selectedCustomer.customerId
                )
                .toPromise()
        );

        await Promise.all(promises);
        this.emailing = false;
        this.snackbarService.showSuccessSnackBar("Emails sent successfully");
    }

    async onScheduleEmail(schedule: ScheduleEmailProps) {
        const selectedCustomers = this.getSelectedCustomers();

        this.emailing = true;

        let body: ScheduleEmailRequest = {
            frequency: schedule.frequency,
            timeZone: schedule.timezone,
            scheduleDateTime: schedule.scheduleDate,
            groupId: this.sharedService.currentUser.groupId,
            customerIds: selectedCustomers.map(customer => customer.customerId),
            createdBy: this.sharedService.currentUser.oid
        };

        return this.scheduledEmailsService
            .scheduleEmail(body)
            .subscribe(() => {
                this.emailing = false;
                this.snackbarService.showSuccessSnackBar("Emails scheduled successfully");
            }, () => {
                this.emailing = false;
                this.snackbarService.showErrorSnackBar("Failed to schedule emails");
            });
    }

    applyFilter(filter: string) {
        this.filter = filter;

        if (!this.data || !this.data.customers) {
            return;
        }
        this.dataSources = this.data.customers.filter((customer) =>
            customer.company.toLowerCase().includes(filter)
        );
        this.focusTextbox();
    }

    customerSelectedClicked(customer: any) {
        this.selectedCustomerMap.set(customer.customerId, customer.checked);
        this.selectedCustomers = this.getSelectedCustomers();
    }

    onSelectAllClicked(selectAll: boolean) {
        if (selectAll) {
            this.selectAllVisibleCustomers();
        } else {
            this.unselectAllVisibleCustomers();
        }
    }

    selectAllVisibleCustomers() {
        const visibleCustomers = this.groupAnalyticsGenerateDialogGridComponent.groupDistributionDataSource.filteredData;

        visibleCustomers.forEach(c => {
            this.selectedCustomerMap.set(c.customerId, true);
        });

        this.updateDataSourcesFromMap();
    }

    unselectAllVisibleCustomers() {
        const visibleCustomers = this.groupAnalyticsGenerateDialogGridComponent.groupDistributionDataSource.filteredData;

        visibleCustomers.forEach(c => {
            this.selectedCustomerMap.set(c.customerId, false);
        });

        this.updateDataSourcesFromMap();
    }

    updateDataSourcesFromMap() {
        if (!this.data || !this.data.customers) {
            return;
        }
        this.dataSources = this.data.customers.map(c => ({
            ...c,
            checked: this.selectedCustomerMap.get(c.customerId) || false
        }));

        setTimeout(() => {
            this.groupAnalyticsGenerateDialogGridComponent?.refreshGroupDistributionDataSource();

            this.selectedCustomers = Array.from(this.selectedCustomerMap.entries())
                .filter(([_, checked]) => checked)
                .map(([id]) => id);
        });
    }

    populateExportDataForCustomer(
        data: any,
        exportData: any[]
    ) {
        for (const fbo of data) {
            for (const fboPrice of fbo.groupCustomerFbos) {
                for (let i = 0; i < fboPrice.prices.length; i++) {
                    const row: any = {};
                    row.ICAO = i === 0 ? fboPrice.icao : '';
                    row['Volume Tier'] = fboPrice.prices[i].volumeTier;
                    row["Product"] = fboPrice.prices[i].product;
                    if (fboPrice.prices[i].priceBreakdownDisplayType === 0) {
                        row.Price = this.decimalPrecisionPipe.transform(fboPrice.prices[i].domPrivate);
                    } else if (fboPrice.prices[i].priceBreakdownDisplayType === 1) {
                        row['International Price'] =
                        this.decimalPrecisionPipe.transform(fboPrice.prices[i].intPrivate)
                        row['Domestic Price'] =
                        this.decimalPrecisionPipe.transform(fboPrice.prices[i].domPrivate);
                    } else if (fboPrice.prices[i].priceBreakdownDisplayType === 2) {
                        row['Commercial Price'] =
                        this.decimalPrecisionPipe.transform(fboPrice.prices[i].domComm);
                        row['Private Price'] =
                        this.decimalPrecisionPipe.transform(fboPrice.prices[i].domPrivate);
                    } else {
                        row['Int/Comm Price'] =
                        this.decimalPrecisionPipe.transform(fboPrice.prices[i].intComm);
                        row['Int/Private Price'] =
                        this.decimalPrecisionPipe.transform(fboPrice.prices[i].intPrivate);
                        row['Dom/Comm Price'] =
                        this.decimalPrecisionPipe.transform(fboPrice.prices[i].domComm);
                        row['Dom/Private Price'] =
                        this.decimalPrecisionPipe.transform(fboPrice.prices[i].domPrivate);
                    }
                    row['Tail Numbers'] = fbo.tailNumbers;
                    exportData.push(row);
                }
                if (!fboPrice.prices.length) {
                    exportData.push({
                        'Dom/Comm': '',
                        'Dom/Private': '',
                        ICAO: fboPrice.icao,
                        'Int/Comm': '',
                        'Int/Private': '',
                        'Tail Numbers': '',
                        'Volume Tier': '',
                    });
                }
            }
        }
    }

    exportReportForCustomer(exportData: any[], company: string) {
        return new Promise((resolve) => {
            // TODO
            const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData); // converts a DOM TABLE element to a worksheet
            const wb: XLSX.WorkBook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Report');

            /* save to file */
            XLSX.writeFile(wb, company + '.csv');

            setTimeout(() => {
                resolve({});
            }, 100);
        });
    }

    focusTextbox() {
        const searchBox = document.getElementById("searchBox") as HTMLInputElement;

        if (searchBox && searchBox.value != "") {
            setTimeout(() => {
                searchBox.focus();
            }, 1);
        }
    }

    onManuallyCheckedChanged(customer:any) {
        if (customer.checked) {
            this.manuallyCheckedCustomerIds.add(customer.customerId);
            this.manuallyUncheckedCustomerIds.delete(customer.customerId);
        } else {
            this.manuallyCheckedCustomerIds.delete(customer.customerId);
            this.manuallyUncheckedCustomerIds.add(customer.customerId);
        }
    }

    onTagSelection(event: any) {
        if (!this.data || !this.data.customers) {
            return;
        }
        this.dataSources = this.data.customers.map(c => {
            const tagMatched = this.doesCustomerMatchTags(c);
            const manual = this.selectedCustomerMap.get(c.customerId);

            const checked = manual === true ? true : manual === false ? false : tagMatched;

            return {
                ...c,
                checked
            };
        });

        setTimeout(() => {
            this.groupAnalyticsGenerateDialogGridComponent?.refreshGroupDistributionDataSource();
        });

        this.selectedCustomers = this.getSelectedCustomers();

        this.focusTextbox();
    }

    private loadTags() {
        if (!this.sharedService?.currentUser?.groupId) {
            return;
        }
        this.tagService.getGroupTags(
            this.sharedService.currentUser.groupId
        )
            .subscribe((data: any) => {
                this.tags = data.map((tg) => ({
                    label: tg,
                    value: tg,
                }));
                // Trigger change detection after tags are loaded to avoid ExpressionChangedAfterItHasBeenCheckedError
                setTimeout(() => {
                    this.cdr.detectChanges();
                }, 0);
            });

    }

}
